import { Skeleton } from '@syncanix/ui';

import { useT } from '@/core/providers/I18nProvider';

import type { ReactElement } from 'react';

/**
 * The full-viewport placeholder the app's BOOT GATES share while they resolve.
 *
 * Extracted from `WorkspaceHydrator` when the Terms-of-Service gate (ADR-0497)
 * became a second gate in the same chain, and the extraction is the anti-flicker
 * mechanism rather than tidiness.
 *
 * Two gates now block the router in sequence — the workspace hydrator waits for
 * the tenant directory, then the terms gate waits for the acceptance answer. Each
 * lives at a different depth in the provider tree, so React unmounts the first
 * one's element and mounts the second's. If the two rendered even slightly
 * different markup, a user would see the placeholder visibly CHANGE partway
 * through a load — a spinner that jumps, resizes, or swaps shape for no reason
 * the user can perceive. Rendering byte-identical markup means the painted pixels
 * do not change at all, so the two waits read as one continuous load.
 *
 * That is also why this is a component rather than a copied `<div>`: a copy would
 * be correct on the day it was written and drift on the first restyle, and the
 * drift would be invisible in review because each file would look fine alone.
 *
 * Distinct from `AppLoading` (the branded `BrandLoader` state) on purpose:
 * `AppLoading` owns the pre-auth frame, this owns the post-auth boot frame, and
 * keeping them separate is what stops the pre-auth animation restarting when the
 * session resolves.
 */
export function BootSkeleton(): ReactElement {
  const { t } = useT();
  return (
    <div
      role="status"
      aria-busy="true"
      className="flex min-h-screen items-center justify-center bg-background"
    >
      <Skeleton shape="circle" className="h-8 w-8" />
      <span className="sr-only">{t('common.loading')}</span>
    </div>
  );
}
